import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetReviewsQuery, useUpdateReviewStatusMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ActionButtons from '../../../components/ActionButtons';
import Pagination from '../../../components/Pagination';
import { showSuccess, showError } from '../../../utils/toast';
import { confirmAction } from '../../../utils/confirm';

const Reviews: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useGetReviewsQuery({ 
    search, 
    status: status !== '' ? status : undefined,
    page 
  });
  
  const [updateReviewStatus] = useUpdateReviewStatusMutation();

  const reviews = data?.data?.data || [];
  const meta = data?.data;

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [search, status]);

  const handleStatusUpdate = async (id: number, newStatus: string) => {
    const confirmed = await confirmAction(
      'Update Status',
      `Are you sure you want to mark this review as ${newStatus}?`,
      `Yes, ${newStatus}`
    );
    
    if (confirmed) {
      try {
        const result = await updateReviewStatus({ id, status: newStatus }).unwrap();
        showSuccess(result.message || 'Review status updated successfully!');
        refetch();
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to update review status. Please try again.');
      }
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Approved': return 'badge-light-success';
      case 'Rejected': return 'badge-light-danger';
      case 'Pending': return 'badge-light-warning';
      default: return 'badge-light-primary';
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Reviews Management</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <span className="text-muted text-hover-primary" onClick={() => navigate('/admin/dashboard')} style={{ cursor: 'pointer' }}>Home</span>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Reviews Management</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card card-flush">
              <div className="card-header align-items-center py-5 gap-2 gap-md-5">
                <div className="card-title">
                  <div className="d-flex align-items-center position-relative my-1">
                    <span className="svg-icon svg-icon-1 position-absolute ms-4">
                      <i className="fas fa-search"></i>
                    </span>
                    <input 
                      type="text" 
                      className="form-control form-control-solid w-250px ps-14" 
                      placeholder="Search Reviews..." 
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="card-toolbar flex-row-fluid justify-content-end gap-5">
                  <div className="w-100 mw-150px">
                    <select 
                      className="form-select form-select-solid" 
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option value="">All Status</option>
                      <option value="Pending">Pending</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th className="min-w-100px">User</th>
                        <th className="min-w-100px">Ad</th>
                        <th className="min-w-100px">Stars</th>
                        <th className="min-w-200px">Comment</th>
                        <th className="min-w-100px">Status</th>
                        <th className="text-end min-w-100px">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {reviews.length > 0 ? (
                        reviews.map((review: any) => (
                          <tr key={review.id}>
                            <td className="d-flex align-items-center">
                              <div className="symbol symbol-circle symbol-50px overflow-hidden me-3">
                                <div className="symbol-label">
                                  <img 
                                    src={review.user?.picture || '/media/avatars/blank.png'} 
                                    alt={review.user?.name} 
                                    className="w-100" 
                                  />
                                </div>
                              </div>
                              <div className="d-flex flex-column">
                                <span className="text-gray-800 text-hover-primary mb-1">{review.user?.name}</span>
                                <span>{review.user?.email}</span>
                              </div>
                            </td>
                            <td>
                                #{review.ad?.ad_id || 'N/A'} <br />
                                {review.ad?.title || 'N/A'}
                            </td>
                            <td>
                              <div className="rating">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <div key={star} className={`rating-label ${star <= review.stars ? 'checked' : ''}`}>
                                    <i className="fas fa-star fs-6"></i>
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td>
                              <div className="text-gray-800 text-hover-primary mb-1 text-truncate" style={{ maxWidth: '200px' }}>
                                {review.comment}
                              </div>
                            </td>
                            <td>
                              <div className={`badge ${getStatusBadgeClass(review.status)}`}>{review.status}</div>
                            </td>
                            <td className="text-end">
                              <ActionButtons 
                                onView={() => navigate(`/admin/reviews/${review.id}`)}
                              />
                              <div className="d-flex justify-content-end gap-2 mt-2">
                                {review.status !== 'Approved' && (
                                  <button 
                                    className="btn btn-icon btn-bg-light btn-active-color-success btn-sm"
                                    onClick={() => handleStatusUpdate(review.id, 'Approved')}
                                    title="Approve"
                                  >
                                    <i className="fas fa-check"></i>
                                  </button>
                                )}
                                {review.status !== 'Rejected' && (
                                  <button 
                                    className="btn btn-icon btn-bg-light btn-active-color-danger btn-sm"
                                    onClick={() => handleStatusUpdate(review.id, 'Rejected')}
                                    title="Reject"
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center">No reviews found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {meta && (
                  <Pagination 
                    currentPage={page} 
                    lastPage={meta.last_page} 
                    total={meta.total}
                    perPage={meta.per_page}
                    onPageChange={setPage} 
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reviews;
